import * as React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';
import { ReactElement, useMemo } from 'react';

import { Money as MoneyType } from '../../types';
import Money from '../../Money';
import { AdditionalServiceType, ServiceButton } from '../types';
import { useTheme } from '../../theme';
import { useRenderers } from '../../renderProps';
import AggregatorService from './Aggregator/AggregatorService';
import { default as BaseButton } from '../../BaseComponents/Button';
import { reactElementAdapter } from '@shared/react';
import { CdnIconNames } from '@shared/ui';

export interface HeaderRenderersProps {
	header: string;
	icon?: React.ReactNode;
	priceFrom?: MoneyType;
	priceTotal?: MoneyType;
	isSelected: boolean;
	isLoading?: boolean;
}

export interface ControlsRenderersProps {
	buttons?: ServiceButton[];
	priceFrom?: MoneyType;
	priceTotal?: MoneyType;
	addButtonText?: string;
	isSelected: boolean;
	showAddButton?: boolean;
	isLoading?: boolean;
	addClick: () => void;
}

export interface ControlsRenderers {
	buttons?: ServiceButton[];
	addButtonText?: string;
	isSelected: boolean;
	isLoading?: boolean;
	showAddButton?: boolean;
	addClick: () => void;
	priceFrom?: MoneyType;
	priceTotal?: MoneyType;
}

export interface Props {
	header: string;
	showAddButton?: boolean;
	description: string | ReactElement;
	isSelected: boolean;
	priceFrom?: MoneyType;
	addButtonText?: string;
	priceTotal?: MoneyType;
	buttons?: ServiceButton[];
	className?: string;
	coverClassName?: string;
	coverBackgroundUrl?: string;
	coverBackgroundColor?: string;
	icon?: React.ReactNode;
	addClick: () => void;
	isLoading?: boolean;
	isExclusive?: boolean;
	descriptionClassName?: string;
	exclusivePlateClassName?: string;
	mainClassName?: string;
	mode?: 'default' | 'aggregator';
	onDelete?: () => void;
	aggregationIcons?: React.ReactNode[] | CdnIconNames[];
	discount?: string;
	renderControls?: (props: ControlsRenderers) => React.ReactNode;
	style?: React.CSSProperties;
	endAdornment?: React.ReactNode;
	serviceType?: AdditionalServiceType;
	insuranceCode?: string;
	servicesCount?: number;
	hideFrom?: boolean;
	showFromZeroIfZeroPrice?: boolean;
	dataRfisc?: string;
	dataTestId?: string;
	buttonsClassName?: string;
	headerClassName?: string;
	headerTextClassName?: string;
	headerPriceClassName?: string;
	displayOrder?: number;
	renderHeaderPrice?: () => React.ReactNode;
	hideFooterButtons?: boolean;
}

const CheckoutAdditionalService: React.FunctionComponent<Props> = props => {
	const theme = useTheme('CheckoutAdditionalService').CheckoutAdditionalService;

	const { t } = useTranslation('CheckoutAdditionalService');

	if (props.mode === 'aggregator') {
		return <AggregatorService {...props} />;
	}

	const { renderCheckoutAdditionalServiceHeader } = useRenderers();

	const description = useMemo<ReactElement>(() => reactElementAdapter(props.description), [
		props.description,
		props.isSelected
	]);

	return (
		<div
			className={cn(theme.service, props.className, { [theme.service_active]: props.isSelected })}
			style={props.style}
			data-insurance-code={props.insuranceCode}
			data-rfisc={props.dataRfisc}
			data-test-id={props.dataTestId ?? 'additional-service'}
		>
			{renderCheckoutAdditionalServiceHeader?.({
				header: props.header,
				icon: props.icon,
				priceFrom: props.priceFrom,
				priceTotal: props.priceTotal,
				isSelected: props.isSelected,
				isLoading: props.isLoading
			}) ?? (
				<>
					{props.isExclusive && !props.isSelected && (
						<span className={cn(theme.exclusivePlate, props.exclusivePlateClassName)}>
							{t('Exclusive')}
						</span>
					)}

					<div className={cn(theme.header, props.headerClassName)}>
						<div className={cn(theme.header__text, props.headerTextClassName)}>
							{props.isSelected && props.icon && <div className={theme.icon}>{props.icon}</div>}

							{props.header}
						</div>

						<div className={cn(theme.header__price, props.headerPriceClassName)}>
							{props.isSelected ? (
								props.priceTotal ? (
									<Money money={props.priceTotal} />
								) : null
							) : props.priceFrom ? (
								<>
									{!props.hideFrom && props.priceFrom?.amount !== 0 && t('from')}

									{props.priceFrom?.amount === 0 ? (
										props.showFromZeroIfZeroPrice ? (
											<span>
												{t('from')}

												<Money money={props.priceFrom} />
											</span>
										) : (
											<>&nbsp;{t('SeatMap:Free')}</>
										)
									) : (
										<Money money={props.priceFrom} />
									)}
								</>
							) : null}
						</div>
					</div>
				</>
			)}

			<div className={cn(theme.main, props.mainClassName)}>
				<div className={cn(theme.content, props.descriptionClassName)}>
					{props.isSelected ? props.children : description}
				</div>

				{props.renderControls?.({
					buttons: props.buttons,
					priceFrom: props.priceFrom,
					priceTotal: props.priceTotal,
					addButtonText: props.addButtonText,
					showAddButton: props.showAddButton,
					isSelected: props.isSelected,
					isLoading: props.isLoading,
					addClick: props.addClick
				}) ?? (
					<div className={cn(theme.buttons, props.buttonsClassName)}>
						{props.isSelected && props.buttons?.length > 0 && (
							<>
								{props.buttons.map(button => (
									<BaseButton
										key={button.text}
										className={button.className}
										isLoading={button.isLoading}
										onClick={() => button.action()}
										variant={button.variant ?? 'text'}
									>
										{button.text}
									</BaseButton>
								))}
							</>
						)}

						{!props.isSelected && props.showAddButton !== false && (
							<BaseButton
								isLoading={props.isLoading}
								variant="optionAction"
								className={theme.add}
								onClick={props.addClick}
							>
								{props.addButtonText ? t(props.addButtonText) : t('Add')}
							</BaseButton>
						)}
					</div>
				)}
			</div>

			{props.endAdornment && props.endAdornment}
		</div>
	);
};

export default CheckoutAdditionalService;
